// /app/frontend/src/components/frugal-v2/components/RawEmailView.jsx
// Full screen raw email viewer with zoom controls (iframe-based)

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { X, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2;
const STEP = 0.1;
// Emails are typically designed for 600-700px wide
const EMAIL_WIDTH = 680;

const RawEmailView = ({ email, isOpen, onClose, t }) => {
  const [zoom, setZoom] = useState(1);
  const [autoFitted, setAutoFitted] = useState(false);
  const containerRef = useRef(null);

  const { htmlContent, textContent } = useMemo(() => {
    const raw = email?.raw_email || {};
    return {
      htmlContent: email?.body_html || raw.html || raw.body_html,
      textContent: email?.body_text || raw.body_text || raw.body || email?.subject
    };
  }, [email]);

  // Auto-fit zoom to container width on open
  useEffect(() => {
    if (!isOpen || autoFitted || !containerRef.current) return;
    const cw = containerRef.current.clientWidth;
    if (cw > 0) {
      // Leave 8px margin, cap between MIN and 1
      const fit = Math.min(1, Math.max(MIN_ZOOM, (cw - 8) / EMAIL_WIDTH));
      setZoom(+fit.toFixed(2));
      setAutoFitted(true);
    }
  }, [isOpen, autoFitted]);

  if (!isOpen || !email) return null;

  const handleZoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, +(z + STEP).toFixed(2)));
  const handleZoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, +(z - STEP).toFixed(2)));
  const handleFit = () => {
    if (containerRef.current) {
      const cw = containerRef.current.clientWidth;
      const fit = Math.min(1, Math.max(MIN_ZOOM, (cw - 8) / EMAIL_WIDTH));
      setZoom(+fit.toFixed(2));
    }
  };

  const hasHtml = htmlContent && htmlContent.includes('<');

  // Build srcDoc for iframe - strip potentially dangerous scripts
  const iframeSrcDoc = hasHtml ? `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=${EMAIL_WIDTH}, initial-scale=1">
<base target="_blank">
<style>
  html, body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; color: #1e293b; background: #ffffff; }
  body { width: ${EMAIL_WIDTH}px; }
  img { max-width: 100%; height: auto; display: inline-block; }
  table { max-width: 100%; }
  td, th { word-break: break-word; }
  a { color: #0ea5e9; }
  script { display: none !important; }
</style>
</head>
<body>${(htmlContent || '').replace(/<script[\s\S]*?<\/script>/gi, '')}</body>
</html>` : '';

  return (
    <div className="fixed inset-0 z-[70] bg-black/95 flex flex-col" onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      <div className="flex items-center gap-2 p-3 bg-slate-800 border-b border-slate-700 shrink-0">
        <span className="text-white font-medium text-sm truncate flex-1">
          📧 {email.subject || email.raw_email?.subject || 'Email Original'}
        </span>
        {hasHtml && (
          <div className="flex items-center gap-1 bg-slate-700/60 rounded-lg px-1">
            <button
              onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
              disabled={zoom <= MIN_ZOOM}
              className="p-1.5 text-white hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed"
              title="Zoom out"
              data-testid="raw-email-zoom-out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs text-slate-200 w-10 text-center font-mono" data-testid="raw-email-zoom-level">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
              disabled={zoom >= MAX_ZOOM}
              className="p-1.5 text-white hover:bg-slate-600 rounded disabled:opacity-30 disabled:cursor-not-allowed"
              title="Zoom in"
              data-testid="raw-email-zoom-in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleFit(); }}
              className="p-1.5 text-white hover:bg-slate-600 rounded"
              title="Ajustar a ventana"
              data-testid="raw-email-zoom-fit"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        )}
        <button
          data-testid="close-raw-email-btn"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
          className="p-2 bg-rose-500 hover:bg-rose-600 rounded-lg active:scale-95 transition-transform"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Content */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-white min-h-0">
        {hasHtml ? (
          <div
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              width: `${EMAIL_WIDTH}px`,
              height: `${100 / zoom}%`,
              transition: 'transform 0.15s ease-out'
            }}
          >
            <iframe
              title="Email Original"
              srcDoc={iframeSrcDoc}
              sandbox="allow-same-origin allow-popups"
              style={{
                width: `${EMAIL_WIDTH}px`,
                height: '100%',
                minHeight: '600px',
                border: 'none',
                display: 'block'
              }}
              data-testid="raw-email-iframe"
            />
          </div>
        ) : (
          <pre
            className="text-slate-700 whitespace-pre-wrap font-sans p-3"
            style={{
              wordBreak: 'break-word',
              fontSize: `${zoom * 14}px`,
              transition: 'font-size 0.15s ease-out'
            }}
          >
            {textContent || t('frugal.emails.noContent')}
          </pre>
        )}
      </div>

      {/* Close button at bottom */}
      <div className="p-3 bg-slate-800 border-t border-slate-700 shrink-0">
        <button
          data-testid="close-raw-email-bottom-btn"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
          className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium active:scale-95 transition-transform"
        >
          {t('frugal.emails.close')}
        </button>
      </div>
    </div>
  );
};

export default RawEmailView;
